import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { prettifyApiError } from "../../shared/api";
import { ErrorBanner } from "../../shared/ui/ErrorBanner";
import { MarketplaceLogo } from "../../shared/ui/MarketplaceLogo";
import { PageHeader } from "../../shared/ui/PageHeader";
import { orderMarketplaceLogo } from "../ordini/components/OrderCard";
import {
  fetchReclami,
  rileggiReclami,
  type Pratica,
  type Reclami,
  type StatoPratica,
  type TipoPratica,
} from "./api";

/** Davide 03/11/2026: «una pagina per le richieste di rimborso a Bracchi,
 *  leggi le mail e carica quelli attivi». Sola lettura: le pratiche le scrive
 *  la lane dalle mail con Bracchi, nessun tasto scrive a Bracchi. */

const STATI: Record<StatoPratica, { testo: string; classe: string }> = {
  manca_nostro: { testo: "Tocca a noi", classe: "border-amber/50 bg-amber-soft" },
  aperta: { testo: "In verifica da SDA", classe: "border-line-2 bg-paper-2" },
  rimborsata: { testo: "Rimborsata", classe: "border-green/40 bg-green-soft" },
  respinta: { testo: "Respinta", classe: "border-red/40 bg-red-soft" },
  chiusa: { testo: "Chiusa", classe: "border-line bg-paper" },
};

const TIPI: Record<TipoPratica, string> = {
  danno: "Merce danneggiata",
  smarrimento: "Colli persi",
  firma: "Firma non del cliente",
  servizio: "Servizio non fatto",
  altro: "Reclamo",
};

type Filtro = "attive" | "noi" | "esiti" | "tutte";

const FILTRI: { chiave: Filtro; testo: string; stati: StatoPratica[] }[] = [
  { chiave: "attive", testo: "Attive", stati: ["manca_nostro", "aperta"] },
  { chiave: "noi", testo: "Tocca a noi", stati: ["manca_nostro"] },
  { chiave: "esiti", testo: "Concluse", stati: ["rimborsata", "respinta", "chiusa"] },
  {
    chiave: "tutte",
    testo: "Tutte",
    stati: ["manca_nostro", "aperta", "rimborsata", "respinta", "chiusa"],
  },
];

const euro = (valore: string) =>
  Number(valore).toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
    useGrouping: "always" as unknown as boolean,
  });

const giorno = (iso: string) =>
  new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "short" });

const giorniFa = (iso: string) => {
  const giorni = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (giorni <= 0) return "oggi";
  if (giorni === 1) return "ieri";
  return `${giorni} giorni fa`;
};

function Stato({ stato }: { stato: StatoPratica }) {
  const s = STATI[stato];
  return (
    <span
      className={`inline-flex shrink-0 rounded-full border px-2 py-px text-[11.5px] font-bold text-ink ${s.classe}`}
    >
      {s.testo}
    </span>
  );
}

function Ordine({ pratica }: { pratica: Pratica }) {
  if (!pratica.ordine) {
    return (
      <p className="text-[12.5px] text-ink-2">
        {pratica.cliente || "Destinatario non scritto"} · ordine non trovato in OM
      </p>
    );
  }
  const logo = orderMarketplaceLogo({ raw: { source: pratica.ordine.canale } });
  return (
    <p className="flex min-w-0 items-center gap-1.5 text-[12.5px] text-ink-2">
      <MarketplaceLogo {...logo} size={16} className="shrink-0" />
      <Link
        to={`/ordini/${encodeURIComponent(pratica.ordine.reference)}`}
        className="font-semibold text-ink hover:underline"
      >
        {pratica.ordine.reference}
      </Link>
      {pratica.cliente && <span className="truncate">· {pratica.cliente}</span>}
    </p>
  );
}

function Dettaglio({ pratica }: { pratica: Pratica }) {
  if (pratica.stato === "manca_nostro") {
    return (
      <p className="text-[13px] leading-snug text-ink">
        <span className="font-bold">Bracchi aspetta: </span>«{pratica.cosa_manca}»
      </p>
    );
  }
  if (pratica.esito) {
    return <p className="text-[13px] leading-snug text-ink">«{pratica.esito}»</p>;
  }
  return (
    <p className="line-clamp-2 text-[13px] leading-snug text-ink-2">
      <span className="font-semibold text-ink">
        {pratica.ultimo_da_noi ? "Noi" : "Bracchi"}, {giorniFa(pratica.ultimo_il)}:{" "}
      </span>
      {pratica.ultimo_testo}
    </p>
  );
}

function Riga({ pratica }: { pratica: Pratica }) {
  return (
    <li className="px-4 py-3.5 transition-colors hover:bg-paper/50 max-md:px-3">
      <div className="grid grid-cols-[minmax(0,15rem)_minmax(0,1fr)_9rem] items-start gap-x-5 max-lg:grid-cols-[minmax(0,1fr)_auto] max-lg:gap-y-2">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2">
            <Stato stato={pratica.stato} />
            <span className="text-[12.5px] font-semibold text-ink-2">{TIPI[pratica.tipo]}</span>
          </p>
          <p className="mt-1 font-mono text-[13.5px] font-bold tracking-tight text-ink">
            {pratica.ldv}
          </p>
          <Ordine pratica={pratica} />
        </div>

        <div className="min-w-0 max-lg:col-span-2 max-lg:row-start-2">
          <Dettaglio pratica={pratica} />
        </div>

        <div className="text-right text-[12px] text-ink-2 max-lg:col-start-2 max-lg:row-start-1">
          {pratica.importo_rimborso && (
            <p className="font-display text-[17px] font-bold tabular-nums text-ink">
              {euro(pratica.importo_rimborso)}
            </p>
          )}
          <p>Aperta il {giorno(pratica.aperta_il)}</p>
          <p>
            {pratica.messaggi} mail · ultima {giorno(pratica.ultimo_il)}
          </p>
        </div>
      </div>
    </li>
  );
}

function Scheletro() {
  return (
    <div aria-hidden="true" className="rounded-lg border border-line bg-card">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="flex gap-4 border-b border-line px-4 py-4 last:border-b-0">
          <div className="h-4 w-40 animate-pulse rounded bg-paper-2" />
          <div className="h-4 flex-1 animate-pulse rounded bg-paper-2" />
        </div>
      ))}
    </div>
  );
}

export function ReclamiPage() {
  const [dati, setDati] = useState<Reclami | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<Filtro>("attive");
  const [rileggo, setRileggo] = useState<"no" | "in_corso" | "accodato" | "gia">("no");

  const ricarica = useCallback(async (signal?: AbortSignal) => {
    try {
      setDati(await fetchReclami(signal));
      setErrore(null);
    } catch (err) {
      if (signal?.aborted) return;
      setErrore(prettifyApiError(err));
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void ricarica(ctrl.signal);
    return () => ctrl.abort();
  }, [ricarica]);

  const scelto = FILTRI.find((f) => f.chiave === filtro);
  const visibili = useMemo(
    () => (dati?.pratiche ?? []).filter((p) => scelto?.stati.includes(p.stato) ?? true),
    [dati, scelto],
  );

  async function rileggi() {
    setRileggo("in_corso");
    try {
      const esito = await rileggiReclami();
      setRileggo(esito.accodato ? "accodato" : "gia");
    } catch (err) {
      setRileggo("no");
      setErrore(prettifyApiError(err));
    }
  }

  const conta = (f: (typeof FILTRI)[number]) =>
    f.stati.reduce((somma, s) => somma + (dati?.conteggi[s] ?? 0), 0);

  return (
    <main className="flex-1 overflow-y-auto px-7 pt-[22px] pb-8 scroll-paper max-md:px-3 max-md:pt-3">
      <PageHeader
        title="Reclami corriere"
        subtitle="Le pratiche di rimborso aperte con Bracchi (SDA): danni, colli persi, firme non del cliente. OM le legge da sole dalle mail ogni ora."
        meta={
          <Link to="/ritiri" className="font-semibold text-ink hover:underline max-md:hidden">
            ← Ritiri
          </Link>
        }
      />

      <ErrorBanner message={errore} className="mb-3" />

      {dati === null && !errore && <Scheletro />}

      {dati !== null && (
        <>
          <div className="mb-5 flex flex-wrap items-end justify-between gap-x-8 gap-y-3 rounded-lg border border-line bg-card px-5 py-4 max-md:px-4">
            <div>
              <p className="text-[13px] text-ink-2">Pratiche ancora aperte</p>
              <p className="font-display text-[34px] font-bold leading-[1.05] tabular-nums tracking-[-0.02em] text-ink max-md:text-[30px]">
                {dati.attive}
              </p>
              <p className="mt-1 max-w-[62ch] text-[12.5px] leading-snug text-ink-2">
                {dati.conteggi.manca_nostro > 0 ? (
                  <>
                    <span className="font-bold text-ink">
                      {dati.conteggi.manca_nostro} aspettano una nostra risposta
                    </span>{" "}
                    (modulo, foto o una domanda di Bracchi) · {dati.conteggi.aperta} in verifica da
                    SDA
                  </>
                ) : (
                  `Nessuna aspetta noi · ${dati.conteggi.aperta} in verifica da SDA`
                )}
              </p>
              {Number(dati.rimborsato) > 0 && (
                <p className="mt-1 text-[12.5px] text-ink-2">
                  Rimborsato da SDA finora:{" "}
                  <span className="font-semibold tabular-nums text-ink">
                    {euro(dati.rimborsato)}
                  </span>
                </p>
              )}
            </div>
            <div className="flex flex-col items-end gap-1.5 max-md:w-full max-md:items-stretch">
              <button
                type="button"
                onClick={() => void rileggi()}
                disabled={rileggo === "in_corso" || dati.aggiornamento_in_corso}
                title="Rilegge subito le mail con Bracchi invece di aspettare il giro di ogni ora"
                className="inline-flex h-[34px] items-center justify-center gap-1.5 rounded-full border border-line-2 bg-card px-3.5 text-[13px] font-semibold text-ink transition-colors hover:border-ink-3 hover:bg-paper-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-4 disabled:cursor-not-allowed disabled:opacity-50 max-md:h-11 max-md:text-[14px]"
              >
                {dati.aggiornamento_in_corso ? "Lettura in corso" : "Rileggi le mail"}
              </button>
              <p className="text-[12px] text-ink-2 max-md:text-center">
                {dati.aggiornate_il
                  ? `Aggiornate ${giorniFa(dati.aggiornate_il)} alle ${new Date(dati.aggiornate_il).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`
                  : "Mai lette: il primo giro parte da solo entro un'ora"}
              </p>
            </div>
          </div>

          {rileggo === "accodato" && (
            <p role="status" className="-mt-2 mb-4 text-[12.5px] text-ink-2">
              Lettura avviata: fra un minuto ricarica la pagina.
            </p>
          )}
          {rileggo === "gia" && (
            <p role="status" className="-mt-2 mb-4 text-[12.5px] text-ink-2">
              Una lettura è già in corso.
            </p>
          )}

          <div role="tablist" aria-label="Filtra le pratiche" className="mb-3 flex flex-wrap gap-2">
            {FILTRI.map((f) => (
              <button
                key={f.chiave}
                type="button"
                role="tab"
                aria-selected={filtro === f.chiave}
                onClick={() => setFiltro(f.chiave)}
                className={[
                  "h-[32px] rounded-full border px-3 text-[13px] font-semibold transition-colors max-md:h-10",
                  filtro === f.chiave
                    ? "border-ink bg-ink text-paper"
                    : "border-line-2 bg-card text-ink hover:bg-paper-2",
                ].join(" ")}
              >
                {f.testo} <span className="tabular-nums opacity-70">{conta(f)}</span>
              </button>
            ))}
          </div>

          {visibili.length === 0 ? (
            <p className="rounded-lg border border-line bg-card px-5 py-6 text-[13.5px] text-ink-2">
              Nessuna pratica qui.
            </p>
          ) : (
            <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-card">
              {visibili.map((p) => (
                <Riga key={p.id} pratica={p} />
              ))}
            </ul>
          )}
        </>
      )}
    </main>
  );
}
